// Tabla de documentos para una tab dada.
// Usa tokens semánticos del theme (bg-card, text-foreground, etc.) para
// respetar el dark mode del dashboard.

import { useEffect, useState } from 'react';
import { Download, Share2, Trash2, FileText, FileImage, FileSpreadsheet, File, Globe, Lock, Users } from 'lucide-react';

import { deleteDocument, listDocuments, updateDocument } from '../api';
import type { DocumentOut, Tab, Visibility } from '../types';

interface Props {
  tab: Tab;
  onShareClick?: (doc: DocumentOut) => void;
  refreshKey?: number;
}

const ICON_BY_TYPE: Record<string, React.ReactNode> = {
  pdf: <FileText size={16} className="text-red-400" />,
  docx: <FileText size={16} className="text-blue-400" />,
  txt: <FileText size={16} className="text-muted-foreground" />,
  csv: <FileSpreadsheet size={16} className="text-green-400" />,
  image: <FileImage size={16} className="text-purple-400" />,
};

const VISIBILITY_LABEL: Record<string, string> = {
  personal: 'Personal',
  org: 'Organización',
  shared: 'Compartido',
  public: 'Link público',
};

// Badges con colores semitransparentes que se adaptan a dark/light
const VISIBILITY_BADGE: Record<string, string> = {
  personal: 'bg-muted text-muted-foreground',
  org: 'bg-blue-500/15 text-blue-400 dark:text-blue-300',
  shared: 'bg-amber-500/15 text-amber-400 dark:text-amber-300',
  public: 'bg-purple-500/15 text-purple-400 dark:text-purple-300',
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function DocumentList({ tab, onShareClick, refreshKey = 0 }: Props) {
  const [docs, setDocs] = useState<DocumentOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(null);
    listDocuments({ tab, limit: 100 })
      .then((d) => { if (!cancel) setDocs(d); })
      .catch((e) => { if (!cancel) setError(String(e?.message || e)); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [tab, refreshKey]);

  async function handleDelete(doc: DocumentOut) {
    if (!confirm(`¿Eliminar "${doc.title}"? Es soft delete (reversible vía admin).`)) return;
    try {
      await deleteDocument(doc.id);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (e: any) {
      alert(`Error: ${e?.message || e}`);
    }
  }

  async function toggleOrgVisibility(doc: DocumentOut) {
    // Toggle rápido entre personal ↔ org. Si está en shared/public, no
    // tocamos (esos requieren ShareDialog para no perder estado).
    if (doc.visibility !== 'personal' && doc.visibility !== 'org') return;
    const next: Visibility = doc.visibility === 'org' ? 'personal' : 'org';
    // Optimistic update
    setDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, visibility: next } : d)));
    try {
      const updated = await updateDocument(doc.id, { visibility: next });
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? updated : d)));
    } catch (e: any) {
      // Revertir
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? doc : d)));
      alert(`Error al cambiar visibilidad: ${e?.message || e}`);
    }
  }

  function VisibilityToggle({ doc }: { doc: DocumentOut }) {
    if (!doc.is_owner) {
      // Read-only badge para no-owners
      return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${VISIBILITY_BADGE[doc.visibility]}`}>
          {VISIBILITY_LABEL[doc.visibility] ?? doc.visibility}
        </span>
      );
    }
    if (doc.visibility === 'shared') {
      return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${VISIBILITY_BADGE.shared}`}>
          <Users size={12} /> Compartido ({doc.share_count})
        </span>
      );
    }
    if (doc.visibility === 'public') {
      return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${VISIBILITY_BADGE.public}`}>
          <Globe size={12} /> Link público
        </span>
      );
    }
    // personal | org → toggle
    const isOrg = doc.visibility === 'org';
    return (
      <button
        type="button"
        onClick={() => toggleOrgVisibility(doc)}
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition ${VISIBILITY_BADGE[doc.visibility]} hover:opacity-80`}
        title={isOrg ? 'Click para hacer personal' : 'Click para compartir con la org'}
      >
        {isOrg ? <Users size={12} /> : <Lock size={12} />}
        {isOrg ? 'Organización' : 'Personal'}
      </button>
    );
  }

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Cargando documentos…</div>;
  if (error) return <div className="p-6 text-sm text-destructive">Error: {error}</div>;
  if (docs.length === 0) {
    const emptyMsg = {
      mine: 'No has subido ningún documento aún.',
      shared_with_me: 'Nadie te ha compartido documentos.',
      org: 'No hay documentos compartidos en la organización.',
      all: 'No hay documentos disponibles.',
    }[tab];
    return (
      <div className="p-12 text-center text-muted-foreground">
        <File size={48} className="mx-auto mb-3 opacity-40" />
        <p>{emptyMsg}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <table className="min-w-full text-sm">
        <thead className="bg-muted/30 text-left text-xs uppercase text-muted-foreground">
          <tr>
            <th className="px-4 py-2 font-medium">Documento</th>
            <th className="hidden px-4 py-2 font-medium sm:table-cell">Visibilidad</th>
            <th className="hidden px-4 py-2 font-medium md:table-cell">Tamaño</th>
            <th className="hidden px-4 py-2 font-medium md:table-cell">Fecha</th>
            <th className="px-4 py-2 text-right font-medium"><span className="sr-only sm:not-sr-only">Acciones</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border text-foreground">
          {docs.map((doc) => (
            <tr key={doc.id} className="hover:bg-muted/30">
              <td className="min-w-0 px-4 py-2.5">
                <div className="flex items-center gap-2">
                  {ICON_BY_TYPE[doc.file_type] ?? <File size={16} className="text-muted-foreground" />}
                  <span className="min-w-0 break-words font-medium">{doc.title}</span>
                </div>
                {doc.description && (
                  <div className="mt-0.5 max-w-[14rem] truncate text-xs text-muted-foreground sm:max-w-md">
                    {doc.description}
                  </div>
                )}
                {/* Metadatos bajo el título: dueño, tags y (en móvil) visibilidad —
                    así la tabla cabe sin scroll horizontal. */}
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="sm:hidden"><VisibilityToggle doc={doc} /></span>
                  <span>{doc.is_owner ? 'Tuyo' : `De ${doc.owner_user_email ?? 'otro miembro'}`}</span>
                  {doc.tags.map((t) => (
                    <span key={t} className="rounded bg-muted px-2 py-0.5 text-foreground">
                      {t}
                    </span>
                  ))}
                  <span className="md:hidden">· {formatBytes(doc.file_size_bytes)}</span>
                </div>
              </td>
              <td className="hidden px-4 py-2 sm:table-cell">
                <VisibilityToggle doc={doc} />
              </td>
              <td className="hidden whitespace-nowrap px-4 py-2 text-xs text-muted-foreground md:table-cell">{formatBytes(doc.file_size_bytes)}</td>
              <td className="hidden whitespace-nowrap px-4 py-2 text-xs text-muted-foreground md:table-cell">
                {new Date(doc.created_at).toLocaleDateString()}
              </td>
              <td className="px-4 py-2">
                <div className="flex flex-col items-end gap-1 sm:flex-row sm:justify-end">
                  {doc.file_url && (
                    <a
                      href={doc.file_url}
                      target="_blank"
                      rel="noopener"
                      className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                      title="Ver / descargar" aria-label="Ver o descargar"
                    >
                      <Download size={14} />
                    </a>
                  )}
                  {(doc.is_owner) && onShareClick && (
                    <button
                      onClick={() => onShareClick(doc)}
                      className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-blue-400"
                      title="Compartir" aria-label="Compartir"
                    >
                      <Share2 size={14} />
                    </button>
                  )}
                  {doc.is_owner && (
                    <button
                      onClick={() => handleDelete(doc)}
                      className="rounded-lg p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      title="Eliminar" aria-label="Eliminar"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
